'use client';

import {
  Text,
  Popover,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  PopoverAnchor,
  useDisclosure,
  Box,
  FlexProps,
  Flex,
} from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { FilterLineIcon } from '@/components/Icons';
import FilterBox from '@/components/UI/Filter/FilterBox';
import {
  IFilterGroup,
  IFilterObject,
} from '@/components/Product/Filters/types';
import FilterItemsGroup from '@/components/UI/Filter/FilterItemsGroup';
import ValueFilter from '@/components/Product/Filters/AllFilters/ValueFilter';
import { isEqual } from 'lodash';
import { ANY_VALUE_RANGE } from '@/constants/product';
import CategoriesFilter from '@/components/Product/Filters/AllFilters/CategoriesFilter';
import { IAppCategory } from '@/types/api/app/category';

interface AllFiltersProps {
  valueRange?: number[];
  onValueRangeChange?: (range: number[]) => void;

  categories?: IAppCategory[];
  categoryOptions?: IAppCategory[];
  onCategoriesChange?: (categories: IAppCategory[]) => void;

  filters?: IFilterObject[];
  onFiltersChange?: (val: IFilterObject[]) => void;

  filterGroups: IFilterGroup[];
}
const AllFilters: FC<AllFiltersProps> = ({
  valueRange,
  onValueRangeChange,
  categories = [],
  categoryOptions = [],
  onCategoriesChange,
  filters = [],
  onFiltersChange,
  filterGroups,
}) => {
  const numOfApplyingFilters = useMemo(() => {
    if (isEqual(valueRange, ANY_VALUE_RANGE))
      return filters.length + categories.length;
    return filters.length + categories.length + 1;
  }, [filters, valueRange, categories]);
  const isActive = !!numOfApplyingFilters;

  const { isOpen, onClose, onToggle } = useDisclosure();

  return (
    <Box zIndex="20">
      <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
        <PopupAnchor
          isActive={isActive}
          onClick={onToggle}
          count={numOfApplyingFilters}
        />
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody w="12.5rem" as={Flex} flexDir="column" gap="1rem">
            <ValueFilter
              range={valueRange}
              onRangeChange={onValueRangeChange}
            />
            <FilterItemsGroup
              filters={filters}
              onFiltersChange={onFiltersChange}
              filterGroups={filterGroups}
            />
            {!!categoryOptions.length && (
              <CategoriesFilter
                categories={categories}
                onCategoriesChange={onCategoriesChange}
                categoryOptions={categoryOptions}
              />
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

interface PopupAnchorProps {
  isActive?: boolean;
  onClick?: FlexProps['onClick'];
  count?: number;
}
const PopupAnchor: FC<PopupAnchorProps> = ({ isActive, onClick, count }) => {
  return (
    <PopoverAnchor>
      <Box>
        <FilterBox
          isActive={isActive}
          onClick={onClick}
          px="0.75rem"
          hideClearIcon
        >
          <FilterLineIcon size="1.25rem" />
          {!!count && <Text>({count})</Text>}
        </FilterBox>
      </Box>
    </PopoverAnchor>
  );
};

export default AllFilters;
